import { BodyText, toast } from "@/components/ui";
import { Field, FieldError, FieldLabel, Input } from "@/components/ui";
import { Tooltip } from "@/components/ui/Tooltip";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components/EditableRecordButton";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import type { Patient } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useUpdatePreferredName } from "../network/usePatientIdentity";

const preferredNameFormSchema = z.object({
  preferredName: z.string(),
});

type PreferredNameFormValues = z.infer<typeof preferredNameFormSchema>;

export const PreferredNameEditor = ({ patient }: { patient: Patient }) => {
  const [isEditing, setIsEditing] = useState(false);

  const { mutateAsync: updatePreferredName, isPending } =
    useUpdatePreferredName(patient.id);

  const form = useForm<PreferredNameFormValues>({
    resolver: zodResolver(preferredNameFormSchema),
    defaultValues: {
      preferredName: patient.strictPreferredName ?? patient.firstName ?? "",
    },
  });

  const handleSubmit = async (values: PreferredNameFormValues) => {
    try {
      await updatePreferredName({
        preferredName: values.preferredName.trim() || null,
      });
      setIsEditing(false);
    } catch {
      toast.error("Failed to update preferred name");
    }
  };

  const handleEditClick = () => {
    form.reset({
      preferredName: patient.strictPreferredName ?? patient.firstName ?? "",
    });
    setIsEditing(true);
  };

  const showInfoIndicator =
    !!patient.strictPreferredName &&
    patient.strictPreferredName !== patient.firstName;

  if (isEditing) {
    return (
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex relative flex-col gap-2 px-3 py-2 w-full"
      >
        <Controller
          control={form.control}
          name="preferredName"
          render={({ field, fieldState }) => (
            <Field>
              <FieldLabel htmlFor="preferredName">Preferred Name</FieldLabel>
              <Input {...field} id="preferredName" autoFocus />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <SubmitCancelButtons
          isSaving={isPending}
          onCancel={() => setIsEditing(false)}
        />
      </form>
    );
  }

  return (
    <EditableRecordButton title="Preferred Name" onClick={handleEditClick}>
      <div className="flex items-center gap-2">
        <BodyText size="sm">
          {patient.strictPreferredName || patient.firstName}
        </BodyText>
        {showInfoIndicator && (
          <Tooltip
            tooltipContentText="Patient's preferred name differs from their legal name."
            side="top"
          >
            <Info className="h-4 w-4 stroke-info shrink-0" />
          </Tooltip>
        )}
      </div>
    </EditableRecordButton>
  );
};
